import { FC, useContext, SyntheticEvent, useMemo, useEffect } from 'react';
import styles from './MigrateStaffAccountForm.module.scss';
import common from '../../util/scss/common.module.scss';
import GlobalContext from '../../contexts/GlobalContext';
import { replacePhrase } from '../../util/helpers';
import MigrateStaffAccountContext, { EMigrateStaffAccountUiState } from '../../contexts/MigrateStaffAccountContext';
import BackCardLayout from '../../layout/BackCardLayout/BackCardLayout';
import { useHasChanged } from '../../util/customHooks';
import { EQueryStringParam } from '../../util/constants';
import { TextField } from '@mui/material';
import { Button } from '@mui/material';

const MigrateStaffAccountForm: FC = () => {
    const { language, fullLoginLink } = useContext(GlobalContext);
    const {
        migrateStaffAccountUiState,
        setMigrateStaffAccountUiState,
        firstName,
        setFirstName,
        firstNameIsValid,
        firstNameValidationMessage,
        firstNameValidationIsActive,
        setFirstNameValidationIsActive,
        lastName,
        setLastName,
        lastNameIsValid,
        lastNameValidationMessage,
        lastNameValidationIsActive,
        setLastNameValidationIsActive,
        email,
        setEmail,
        emailIsValid,
        emailValidationMessage,
        emailValidationIsActive,
        setEmailValidationIsActive,
        startedWithValidEmail,
        startedWithValidNames,
        disableInfoSubmit,
        submitInfoForm,
    } = useContext(MigrateStaffAccountContext);

    const emailHasChanged = useHasChanged(email);

    const loginWithHint = useMemo(
        () => `${fullLoginLink}&${EQueryStringParam.loginHint}=${email}`,
        [fullLoginLink, email],
    );

    const uiEmailIsValid = useMemo(
        () => (emailValidationIsActive ? emailIsValid : true),
        [emailValidationIsActive, emailIsValid],
    );

    const uiFirstNameIsValid = useMemo(
        () => (firstNameValidationIsActive ? firstNameIsValid : true),
        [firstNameValidationIsActive, firstNameIsValid],
    );

    const uiLastNameIsValid = useMemo(
        () => (lastNameValidationIsActive ? lastNameIsValid : true),
        [lastNameValidationMessage, lastNameIsValid],
    );

    const formattedTitle = useMemo(() => {
        if (startedWithValidEmail) {
            if (startedWithValidNames) {
                return language.staffAccountMigration.email.title.confirm;
            }
            return language.staffAccountMigration.email.title.confirmNoName;
        } else {
            if (startedWithValidNames) {
                return language.staffAccountMigration.email.title.add;
            }
            return language.staffAccountMigration.email.title.addNoName;
        }
    }, [startedWithValidEmail, startedWithValidNames]);

    const formattedDesc = useMemo(() => {
        if (startedWithValidEmail) {
            if (startedWithValidNames) {
                return replacePhrase(language.staffAccountMigration.email.desc.confirm, '<firstName>', firstName);
            }
            return language.staffAccountMigration.email.desc.confirmNoName;
        } else {
            if (startedWithValidNames) {
                return replacePhrase(language.staffAccountMigration.email.desc.add, '<firstName>', firstName);
            }
            return language.staffAccountMigration.email.desc.addNoName;
        }
    }, [startedWithValidEmail, startedWithValidNames, firstName]);

    const handleFirstNameChange = (value: string) => {
        if (!firstNameValidationIsActive) {
            setFirstNameValidationIsActive(true);
        }
        setFirstName(value);
    };

    const handleLastNameChange = (value: string) => {
        if (!lastNameValidationIsActive) {
            setLastNameValidationIsActive(true);
        }
        setLastName(value);
    };

    const handleSubmit = (event: SyntheticEvent) => {
        event.preventDefault();
        submitInfoForm();
    };

    useEffect(() => {
        if (migrateStaffAccountUiState === EMigrateStaffAccountUiState.userExists) {
            if (emailHasChanged) {
                setMigrateStaffAccountUiState(EMigrateStaffAccountUiState.ready);
            }
        }
    }, [email, migrateStaffAccountUiState]);

    return (
        <BackCardLayout>
            <header>
                <h1 className={`${common.pageHeader} ${common.withSubhead}`}>{formattedTitle}</h1>
                <p className={common.pageSubhead}>{formattedDesc}</p>
            </header>
            <form>
                <TextField
                    label={language.common.email}
                    value={email}
                    type="email"
                    onChange={(event) => {
                        setEmail(event.target.value);
                    }}
                    error={!uiEmailIsValid}
                    helperText={!uiEmailIsValid && emailValidationMessage}
                    onBlur={() => {
                        setEmailValidationIsActive(true);
                    }}
                    margin="normal"
                    fullWidth
                    autoFocus
                />
                {migrateStaffAccountUiState === EMigrateStaffAccountUiState.userExists && (
                    <div className={styles.login}>
                        <a href={loginWithHint}>{language.staffAccountMigration.email.login}</a>
                    </div>
                )}
                {!startedWithValidNames && (
                    <>
                        <TextField
                            label={language.createAccount.form.firstName}
                            value={firstName}
                            error={!uiFirstNameIsValid}
                            helperText={!uiFirstNameIsValid && firstNameValidationMessage}
                            onChange={(event) => handleFirstNameChange(event.target.value)}
                            margin="normal"
                            fullWidth
                        />
                        <TextField
                            label={language.createAccount.form.lastName}
                            value={lastName}
                            error={!uiLastNameIsValid}
                            helperText={!uiLastNameIsValid && lastNameValidationMessage}
                            onChange={(event) => handleLastNameChange(event.target.value)}
                            margin="normal"
                            fullWidth
                        />
                    </>
                )}
                <section className={styles.cta}>
                    <Button
                        disabled={disableInfoSubmit}
                        onClick={handleSubmit}
                        type="submit"
                        variant="contained"
                        size="large"
                    >
                        {language.common.next}
                    </Button>
                </section>
            </form>
        </BackCardLayout>
    );
};

export default MigrateStaffAccountForm;
